import { useEffect, useState } from "react";
import { Loader2, Satellite, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PRAYER_LABELS } from "@/lib/format";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const KEYS = ["imsak", "subuh", "terbit", "dhuha", "dzuhur", "ashar", "maghrib", "isya"];

export default function JadwalSholat() {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadPreview = () => api.get("/prayer/today").then((r) => setPreview(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/prayer/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e)));
    loadPreview();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings, offsets: Object.fromEntries(KEYS.map((k) => [k, Number(settings.offsets?.[k] || 0)])) };
      const { data } = await api.put("/prayer/settings", payload);
      setSettings(data);
      toast.success("Pengaturan jadwal sholat disimpan");
      loadPreview();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="text-slate-500">Memuat...</p>;

  const Mode = ({ value, icon: Icon, title, desc }) => (
    <button
      type="button"
      onClick={() => setSettings({ ...settings, mode: value })}
      className={`text-left rounded-3xl border p-5 transition-colors ${settings.mode === value ? "emerald-bg text-white border-transparent" : "bg-white border-emerald-900/10 hover:border-emerald-700/40"}`}
      data-testid={`prayer-mode-${value}`}
    >
      <Icon size={20} className={settings.mode === value ? "text-[#d4af37]" : "text-emerald-700"} />
      <p className="mt-3 font-bold">{title}</p>
      <p className={`text-sm mt-1 ${settings.mode === value ? "text-emerald-200" : "text-slate-500"}`}>{desc}</p>
    </button>
  );

  return (
    <div data-testid="admin-prayer-page">
      <PageHeader title="Jadwal Sholat" subtitle="Atur sumber jadwal sholat yang tampil di website">
        <Button onClick={save} disabled={saving} className="rounded-full emerald-bg hover:bg-[#145c48]" data-testid="prayer-save-btn">
          {saving ? <Loader2 className="animate-spin" size={16} /> : "Simpan Pengaturan"}
        </Button>
      </PageHeader>
      <div className="grid md:grid-cols-2 gap-4">
        <Mode value="auto" icon={Satellite} title="Otomatis (Kab. Ciamis)" desc="Mengambil jadwal harian dari data Kemenag RI untuk wilayah Kabupaten Ciamis." />
        <Mode value="manual" icon={PencilLine} title="Manual" desc="Pengurus menetapkan sendiri waktu sholat setiap hari." />
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-emerald-900/10 p-6">
          <h3 className="font-semibold text-emerald-950">{settings.mode === "manual" ? "Waktu Sholat Manual" : "Koreksi Waktu (menit)"}</h3>
          <p className="text-sm text-slate-500 mt-1">
            {settings.mode === "manual" ? "Masukkan waktu dalam format 24 jam (HH:MM)." : "Tambah/kurangi menit dari jadwal otomatis, mis. +2 untuk ikhtiyat."}
          </p>
          <div className="grid sm:grid-cols-4 gap-4 mt-5">
            {KEYS.map((k) => (
              <div key={k} className="space-y-1.5">
                <Label htmlFor={`p-${k}`}>{PRAYER_LABELS[k]}</Label>
                {settings.mode === "manual" ? (
                  <Input id={`p-${k}`} type="time" value={settings.manual?.[k] || ""} onChange={(e) => setSettings({ ...settings, manual: { ...settings.manual, [k]: e.target.value } })} data-testid={`prayer-manual-${k}`} />
                ) : (
                  <Input id={`p-${k}`} type="number" value={settings.offsets?.[k] ?? 0} onChange={(e) => setSettings({ ...settings, offsets: { ...settings.offsets, [k]: e.target.value } })} data-testid={`prayer-offset-${k}`} />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl emerald-bg pattern-overlay text-white p-6" data-testid="prayer-preview">
          <p className="text-xs uppercase tracking-widest text-[#80b996] font-semibold">Tampil di website</p>
          <p className="text-sm text-emerald-200 mt-1">{preview?.hari}, {preview?.tanggal}</p>
          <ul className="mt-4 space-y-2">
            {KEYS.map((k) => (
              <li key={k} className="flex justify-between text-sm border-b border-white/10 pb-2 last:border-0">
                <span className="text-emerald-100">{PRAYER_LABELS[k]}</span>
                <span className="font-bold gold-text" data-testid={`prayer-preview-${k}`}>{preview?.times?.[k] || "--:--"}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[11px] text-emerald-300/70">Sumber: {preview?.source}</p>
        </div>
      </div>
    </div>
  );
}
